import React, { useEffect, useState } from 'react';
import { Download, X, AlertTriangle, Loader2 } from 'lucide-react';
import { bulkDownloadApi } from '../lib/api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  fileIds: string[];
  /** User-friendly description shown in the modal header, e.g. "S9 folder" */
  context: string;
}

function formatBytes(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatSeconds(s: number): string {
  if (s < 60) return `~${s} second${s === 1 ? '' : 's'}`;
  const m = Math.ceil(s / 60);
  return `~${m} minute${m === 1 ? '' : 's'}`;
}

/**
 * Pre-flight confirmation for bulk downloads.
 *
 * Shows file count + total size + ETA so users know what they're about to do.
 * Refuses up front if the selection exceeds the server cap (200 files OR 5 GB)
 * — better than starting a job that'll fail mid-stream.
 *
 * Critical UX: this kills the "click Download 5 times because I don't know
 * if anything is happening" pattern that triggered server crashes on May 4.
 */
export function BulkDownloadConfirmModal({ isOpen, onClose, onConfirm, fileIds, context }: Props) {
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState<{
    file_count: number;
    total_bytes: number;
    estimated_seconds: number;
    exceeds_limit: boolean;
    limit_reason: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || fileIds.length === 0) {
      setEstimate(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    bulkDownloadApi.estimate(fileIds)
      .then(res => {
        if (!cancelled) setEstimate(res.data?.data || null);
      })
      .catch(err => {
        if (!cancelled) setError(err?.response?.data?.error || 'Could not estimate download size');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, fileIds]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[180] flex items-center justify-center bg-black/60">
      <div className="bg-card border border-border rounded-lg w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Download size={18} className="text-blue-600" />
            <h2 className="text-base font-semibold">Download {context}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 size={14} className="animate-spin" /> Calculating size and time…
            </div>
          ) : error ? (
            <div className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded p-3 border border-red-200 dark:border-red-800">
              {error}
            </div>
          ) : estimate ? (
            <>
              {estimate.exceeds_limit ? (
                <div className="flex gap-2 items-start text-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded p-3 text-amber-800 dark:text-amber-300">
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  <p>{estimate.limit_reason}</p>
                </div>
              ) : (
                <>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <dt className="text-muted-foreground">Files</dt>
                    <dd className="font-medium">{estimate.file_count}</dd>
                    <dt className="text-muted-foreground">Total size</dt>
                    <dd className="font-medium">{formatBytes(estimate.total_bytes)}</dd>
                    <dt className="text-muted-foreground">Estimated time</dt>
                    <dd className="font-medium">{formatSeconds(estimate.estimated_seconds)}</dd>
                  </dl>
                  <p className="text-xs text-muted-foreground border-t border-border pt-3">
                    Files will be packaged into a single zip. Once you click Download, you can keep
                    using Orbit while it prepares — a progress card will appear in the bottom-right.
                  </p>
                </>
              )}
            </>
          ) : null}
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded border border-border hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={() => { if (estimate && !estimate.exceeds_limit) onConfirm(); }}
            disabled={loading || !!error || !estimate || estimate.exceeds_limit}
            className="px-4 py-2 text-sm font-medium rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Download
          </button>
        </div>
      </div>
    </div>
  );
}
